import React from "react";
import {styled} from "@reside/ui";

import {TemplateResultInfo} from "../types";
import {parseDateAsUtc} from "../utils";

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
`;

const PhaseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PhaseLabel = styled.span`
  font-size: 11px;
  color: #596973;
  width: 60px;
  text-align: right;
  flex-shrink: 0;
`;

const BarContainer = styled.div`
  flex: 1;
  height: 16px;
  background: #ebedf0;
  border-radius: 3px;
  overflow: hidden;
`;

const Bar = styled.div<{widthPercent: number; color: string; active?: boolean}>`
  height: 100%;
  width: ${({widthPercent}) => widthPercent}%;
  min-width: ${({widthPercent}) => (widthPercent > 0 ? "4px" : "0")};
  background: ${({color}) => color};
  border-radius: 3px;
  transition: width 0.3s ease;

  ${({active}) =>
    active
      ? `
    background-image: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 5px,
      rgba(255, 255, 255, 0.3) 5px,
      rgba(255, 255, 255, 0.3) 10px
    );
    animation: stripe-move 1s linear infinite;

    @keyframes stripe-move {
      0% { background-position: 0 0; }
      100% { background-position: 20px 0; }
    }
  `
      : ""}
`;

const Duration = styled.span`
  font-size: 11px;
  color: #596973;
  width: 50px;
  flex-shrink: 0;
`;

function durationSeconds(start: string | null, end: string | null): number | null {
  if (!start) return null;
  if (!end) return null;
  return (parseDateAsUtc(end) - parseDateAsUtc(start)) / 1000;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "...";
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

interface Phase {
  label: string;
  start: string | null;
  end: string | null;
  color: string;
}

export const ProcessingTimeline = ({
  result,
}: {
  result: TemplateResultInfo;
}): JSX.Element | null => {
  const phases: Phase[] = [
    {
      label: "Detect",
      start: result.created,
      end: result.start_template_processing,
      color: "#2bb7d9",
    },
    {
      label: "Process",
      start: result.start_template_processing,
      end: result.end_template_processing,
      color: "#1bd760",
    },
    {
      label: "Publish",
      start: result.start_template_publishing,
      end: result.end_template_publishing,
      color: "#8482f9",
    },
  ];

  const durations = phases.map((p) => durationSeconds(p.start, p.end));
  const maxDuration = Math.max(...durations.filter((d): d is number => d !== null), 1);

  return (
    <TimelineContainer>
      {phases.map((phase, i) => {
        const dur = durations[i];
        const isActive = phase.start !== null && phase.end === null;
        const widthPercent = dur !== null ? (dur / maxDuration) * 100 : isActive ? 100 : 0;

        return (
          <PhaseRow key={phase.label}>
            <PhaseLabel>{phase.label}</PhaseLabel>
            <BarContainer>
              <Bar
                widthPercent={widthPercent}
                color={phase.color}
                active={isActive}
              />
            </BarContainer>
            <Duration>{isActive ? "running" : formatDuration(dur)}</Duration>
          </PhaseRow>
        );
      })}
    </TimelineContainer>
  );
};
